/**
 * A profile picture from a file the person chose: cut to the middle square and made small (256 px),
 * kept as a data: URL inside the profile's record. Anything that is not a picture, or is bigger than
 * 5 MB, is refused.
 */
export const PICTURE_SIZE = 256;
export const PICTURE_MAX_BYTES = 5 * 1024 * 1024;
export const PICTURE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export class PictureError extends Error {}

function load(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new PictureError('not a picture'));
    };
    img.src = url;
  });
}

export async function pictureFromFile(file: File, size = PICTURE_SIZE): Promise<string> {
  if (!PICTURE_TYPES.includes(file.type)) throw new PictureError('type');
  if (file.size > PICTURE_MAX_BYTES) throw new PictureError('size');
  const img = await load(file);
  const side = Math.min(img.naturalWidth, img.naturalHeight);
  if (!side) throw new PictureError('empty');
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, size, size);
  const webp = c.toDataURL('image/webp', 0.86);
  return webp.startsWith('data:image/webp') ? webp : c.toDataURL('image/png');
}

/** Let the person pick a picture file (resolves null when they close the dialog). */
export function pickPicture(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = PICTURE_TYPES.join(',');
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.addEventListener('cancel', () => resolve(null));
    input.click();
  });
}
